import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { HelpBox, HelpBoxBoldText } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { Command, GLOBAL_OPTION, ProcessStatus } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import CompanyService from 'services/CompanyService';
import { ConnectVerifierModel, VerificationType } from 'services/EpdClient';
import { useConnectEpdIndividualVerifier } from 'services/api/mutations';
import axios, { AxiosError } from 'axios';
import styled from 'styled-components';
import { EPDVerifierCompanyModel, EPDVerifierModel, Option, VerifierType } from 'types/types';

import { useQueryClient } from '@tanstack/react-query';
import { useVerifierMarketplaceCountriesLookupWithGlobal } from 'hooks/lookups';
import { useScrollShadow } from 'hooks/useScrollShadow';
import { ScrollShadowDialog } from 'styles/Styles.styled';
import EpdRoutes from 'routes/EpdRoutes';
import { QUERY_KEYS } from 'services/api/constants';
import { HyperLink } from 'styles/Styles.styled';
import VerifierCompaniesList from './VerifierCompaniesList';

const VerifierFilterOption = {
  All: 'All',
  ApprovedIndividual: 'Approved Individual Verifiers',
  CertificationBodies: 'Accredited Certification Bodies',
};

type TProps = {
  epdId: string;
  epdVersionId: string;
  epdVerifier: EPDVerifierModel | null;
  onHide: any;
  isOpened: boolean;
};

const EpdVerifierSelectorDialog = ({ epdId, epdVersionId, epdVerifier, onHide, isOpened, ...props }: TProps) => {
  const { companyId } = useContext(CompanyContext);
  const history = useHistory();
  const [verifiers, setVerifiers] = useState<EPDVerifierCompanyModel[]>([]);
  const [filteredVerifierCompanies, setFilteredVerifierCompanies] = useState<EPDVerifierCompanyModel[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Option | undefined>();
  const [selectedVerifier, setSelectedVerifier] = useState<EPDVerifierCompanyModel | undefined>();
  const [inquiryStatus, setInquiryStatus] = useState<[ProcessStatus, Command]>([ProcessStatus.None, Command.None]);

  const { isScrolled, contentRef } = useScrollShadow();

  const queryClient = useQueryClient();
  const verifierTypeOptions = [
    { value: VerifierFilterOption.All, label: VerifierFilterOption.All },
    { value: VerifierFilterOption.ApprovedIndividual, label: VerifierFilterOption.ApprovedIndividual },
    { value: VerifierFilterOption.CertificationBodies, label: VerifierFilterOption.CertificationBodies },
  ] as Option[];

  const [selectedVerifierType, setSelectedVerifierType] = useState<Option>(verifierTypeOptions[0]);

  const connectVerifierMutation = useConnectEpdIndividualVerifier(epdVersionId, (error) => {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ detail?: string }>;
      if (axiosError.response?.status === 403) {
        if (axiosError.response.data?.detail === 'You do not have permission to view this EPD.') {
          toaster({ severity: 'error', summary: 'Access denied', details: 'You do not have permission to view this EPD.' });
          onHide();
          history.push(EpdLinks.dashboard());
          return;
        }
        toaster({
          severity: 'error',
          summary: 'Error',
          details: 'Only the EPD Owner is allowed to perform this action.',
        });
        return;
      }
    }
    setFilteredVerifierCompanies((prev) =>
      prev.map((c) => (c.id === selectedVerifier?.id ? { ...c, errorText: 'Something went wrong.' } : c))
    );
  });

  const countries = useVerifierMarketplaceCountriesLookupWithGlobal().data || [];

  const onChangeVerifierCountry = async (propertyName: string, val: any) => {
    if (!countries.length) return;
    setSelectedCountry(countries.find((c) => c.value === val));
  };

  const onChangeVerifierType = async (propertyName: string, val: any) => {
    setSelectedVerifierType(verifierTypeOptions.find((v) => v.value === val)!);
  };

  const onAddVerifier = async (verifierId: string) => {
    try {
      const verifier = verifiers.find((v) => v.id === verifierId);
      if (!verifier) {
        throw new Error('Verifier is selected but not found in data set');
      }

      const connectVerifierModel: ConnectVerifierModel = {
        epdId,
        certificationBodyCompanyId: verifier.verifierType === VerifierType.Body ? verifierId : null,
        individualVerifierMembershipId: verifier.verifierType === VerifierType.Individual ? verifierId : null,
      };
      setInquiryStatus([ProcessStatus.Fetching, Command.Connect]);
      await connectVerifierMutation.mutateAsync(connectVerifierModel);
      setInquiryStatus([ProcessStatus.Success, Command.Connect]);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_COMPANY_VERIFIERS] });
      toaster({
        severity: 'success',
        summary: 'The verifier was added to the EPD!',
        details: 'Now the EPD Verifier has an access to the EPD.',
      });
    } catch (error) {
      setInquiryStatus([ProcessStatus.Error, Command.Connect]);
    }
  };

  useEffect(() => {
    const fetchCompanyVerifiers = async () => {
      let result: EPDVerifierCompanyModel[] = [];

      switch (selectedVerifierType.value) {
        case VerifierFilterOption.All:
          result = await CompanyService.getCompanyVerifiers(
            companyId,
            selectedCountry?.value,
            selectedCountry?.label === GLOBAL_OPTION.label ? true : undefined,
            epdVersionId,
            VerificationType.EPDVerification
          );
          break;
        case VerifierFilterOption.ApprovedIndividual:
          result = await CompanyService.getCompanyIndividualVerifiers(
            companyId,
            selectedCountry?.value,
            selectedCountry?.label === GLOBAL_OPTION.label ? true : undefined,
            epdVersionId,
            VerificationType.EPDVerification
          );
          break;
        case VerifierFilterOption.CertificationBodies:
          result = await CompanyService.getCompanyCertificationBodies(
            companyId,
            selectedCountry?.value,
            selectedCountry?.label === GLOBAL_OPTION.label ? true : undefined,
            epdVersionId,
            VerificationType.EPDVerification
          );
          break;
        default:
          break;
      }

      setVerifiers(result);
      setFilteredVerifierCompanies(result);
    };

    fetchCompanyVerifiers();
  }, [companyId, selectedCountry, selectedVerifierType, isOpened]);

  const handleSearchVerifierCompanies = (event: any) => {
    const searchTerm = event.target.value.toLowerCase();
    if (!searchTerm) {
      setFilteredVerifierCompanies(verifiers);
      return;
    }
    setFilteredVerifierCompanies((prev) =>
      verifiers.filter(
        (item) => item.name?.toLowerCase().includes(searchTerm) || item.email?.toLowerCase().includes(searchTerm)
      )
    );
  };

  return (
    <StyledDialog
      resizable={false}
      $isScrolled={isScrolled}
      header="EPD Verifier"
      visible={isOpened}
      contentStyle={!!filteredVerifierCompanies.length ? {} : { overflowY: 'initial' }}
      onHide={() => {
        setSelectedVerifier(undefined);
        setSelectedCountry(undefined);
        setSelectedVerifierType(verifierTypeOptions[0]);
        onHide();
      }}
      {...props}
    >
      <DialogContentContainer ref={contentRef}>
        <HelpBox>
          <span>
            Visit the{' '}
            <HyperLink target="_blank" rel="noopener noreferrer" href={EpdRoutes.resources.contactVerifier}>
              Contact verifier
            </HyperLink>{' '}
            page to browse approved verifiers and send an email with the required collaboration details to the selected
            verifier(s).
          </span>
          <span>
            After confirmation, use the <HelpBoxBoldText>"Add verifier"</HelpBoxBoldText> button here to assign the verifier
            to your EPD.
          </span>
        </HelpBox>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Country"
            name="verifierCountry"
            options={countries}
            value={selectedCountry}
            onChanged={onChangeVerifierCountry}
            placeholder="Select"
            isClearable={true}
          />
        </DialogContentRow>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Verifier type"
            name="verifierType"
            options={verifierTypeOptions}
            value={selectedVerifierType}
            onChanged={onChangeVerifierType}
            placeholder="Select"
            isClearable={false}
          />
        </DialogContentRow>
        <DialogContentRow>
          <WizardTextInputControlledComponent
            label="Verifier name or email"
            tooltipPosition={'right'}
            value={undefined}
            placeholder="Type here to search"
            onChange={handleSearchVerifierCompanies}
            onChanged={() => {}}
            name="nameOrEmail"
            maxLength={150}
          />
        </DialogContentRow>
        <VerifierCompaniesList
          epdVerifier={epdVerifier}
          verifierCompanies={filteredVerifierCompanies}
          selectedVerifierCompany={selectedVerifier}
          onSelectVerifierCompany={setSelectedVerifier}
          onAddVerifier={onAddVerifier}
          buttonsAreDisabled={
            (inquiryStatus[0] === ProcessStatus.Fetching && inquiryStatus[1] === Command.Inquiry) ||
            (inquiryStatus[0] === ProcessStatus.Fetching && inquiryStatus[1] === Command.Connect)
          }
        />
      </DialogContentContainer>
    </StyledDialog>
  );
};

const StyledDialog = styled(ScrollShadowDialog)`
  width: 35rem;

  .p-dialog-header-close svg path {
    fill: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 2rem 2rem;
  scrollbar-gutter: stable;
`;

const DialogContentRow = styled.div`
  margin-top: 25px;
  margin-bottom: 8px;
`;

export default EpdVerifierSelectorDialog;
